import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import RestaurantDashboardLayout from './RestaurantDashboardLayout';
import {
  createPurchasesFromGroceryCheckout,
  createRestaurantGroceryOrder,
  createRestaurantGroceryRazorpayOrder,
  createStoreGroceryOrder,
  createStoreGroceryRazorpayOrder,
  recordRestaurantGroceryPaymentSuccess,
  recordStoreGroceryPaymentSuccess,
} from './groceryApi';
import {
  openRazorpayCheckout,
  RazorpayCheckoutCancelledError,
  RazorpayPaymentFailedError,
  RAZORPAY_PAYMENT_FAILED_MESSAGE,
} from './razorpayCheckout';
import {
  BUY_GROCERY_PATH,
  buildStoreOrderPayload,
  cartGrandTotalFromCart,
  cartGstFromSubtotal,
  cartLinesFromCart,
  cartPlatformFeeFromSubtotal,
  cartSubtotalFromCart,
  clearBuyGroceryCartStorage,
  getBuyGroceryBuyerContext,
  GROCERY_GST_PERCENT,
  getGroceryItemStockLimit,
  groupCartByStore,
  loadBuyGroceryCart,
  saveBuyGroceryCart,
  validateGroceryCartQuantity,
} from './buyGroceryCartStorage';
import './css/BuyGrocery.css';
import './css/PlaceOrder.css';

const PURCHASES_PATH = '/home?tab=purchase';

const BuyGroceryCart = () => {
  const navigate = useNavigate();
  const buyer = useMemo(() => getBuyGroceryBuyerContext(), []);
  const buyerId = buyer.buyerId;
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  }, []);
  const createdById = Number(user?.userId || user?.id || user?.User_Id || 0);

  const [cart, setCart] = useState(() => loadBuyGroceryCart(buyer));
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    saveBuyGroceryCart(buyer, cart);
  }, [cart, buyer, buyerId]);

  const cartLines = useMemo(() => cartLinesFromCart(cart), [cart]);
  const cartSubtotal = useMemo(() => cartSubtotalFromCart(cart), [cart]);
  const cartGst = useMemo(() => cartGstFromSubtotal(cartSubtotal), [cartSubtotal]);
  const cartPlatformFee = useMemo(
    () => cartPlatformFeeFromSubtotal(cartSubtotal),
    [cartSubtotal]
  );
  const cartGrandTotal = useMemo(() => cartGrandTotalFromCart(cart), [cart]);
  const cartByStore = useMemo(() => groupCartByStore(cartLines), [cartLines]);
  const cartStoreIds = useMemo(
    () => [...new Set(cartLines.map((line) => String(line.supermarketId)))],
    [cartLines]
  );

  const updateQuantity = useCallback((line, nextQty) => {
    const groceryItemId = line?.groceryItemId ?? line;
    const key = String(groceryItemId);
    const stockLimit = getGroceryItemStockLimit(line);
    const itemName = line?.itemName;
    if (nextQty <= 0) {
      setCart((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setSubmitError('');
      return;
    }
    if (!validateGroceryCartQuantity(itemName, nextQty, stockLimit)) {
      return;
    }
    setCart((prev) => ({
      ...prev,
      [key]: { ...prev[key], quantity: nextQty },
    }));
    setSubmitError('');
  }, []);

  const clearCart = useCallback(() => {
    setCart({});
    clearBuyGroceryCartStorage(buyer);
    setSubmitError('');
  }, [buyer]);

  const handleCheckout = async () => {
    setSubmitError('');
    if (!buyerId) {
      setSubmitError(buyer.isStore ? 'Select a store first.' : 'Select a restaurant first.');
      return;
    }
    if (!createdById) {
      setSubmitError('Please sign in again.');
      return;
    }
    if (cartLines.length === 0) {
      setSubmitError('Your cart is empty.');
      return;
    }

    let orderData = null;

    try {
      setIsSubmitting(true);
      const isMultiStore = cartByStore.length > 1;
      const storeOrdersPayload = cartByStore.map((group) => {
        const payload = buildStoreOrderPayload(group);
        if (!isMultiStore) {
          payload.grandTotal = cartGrandTotal;
        }
        return payload;
      });

      const razorpayOrder = buyer.isStore
        ? await createStoreGroceryRazorpayOrder(buyerId, { amount: cartGrandTotal })
        : await createRestaurantGroceryRazorpayOrder(buyerId, { amount: cartGrandTotal });

      const paymentResponse = await openRazorpayCheckout({
        keyId: razorpayOrder.keyId,
        orderId: razorpayOrder.razorpayOrderId,
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        testMode: Boolean(razorpayOrder.testMode),
        name: buyer.isStore
          ? user?.storeName || localStorage.getItem('storeName') || 'Grocery purchase'
          : user?.restaurantName || 'Grocery purchase',
        description: 'Grocery order',
        prefill: {
          name: user?.name || user?.userName || user?.fullName || '',
          email: user?.email || '',
          contact: String(user?.mobile || user?.phone || user?.contactNo || ''),
        },
      });

      const orderPayload = {
        orderStatusCode: 'NEW',
        orderType: 'DELIVERY',
        createdBy: createdById,
        grandTotal: cartGrandTotal,
        platformFee: isMultiStore ? cartPlatformFee : 0,
        storeOrders: storeOrdersPayload,
      };

      orderData = buyer.isStore
        ? await createStoreGroceryOrder(buyerId, orderPayload)
        : await createRestaurantGroceryOrder(buyerId, orderPayload);

      if (buyer.isStore) {
        await recordStoreGroceryPaymentSuccess(buyerId, {
          createdBy: createdById,
          amount: cartGrandTotal,
          groceryOrderId: orderData?.groceryOrderId,
          masterOrderId: orderData?.masterOrderId,
          razorpayOrderId: paymentResponse.razorpay_order_id,
          razorpayPaymentId: paymentResponse.razorpay_payment_id,
          razorpaySignature: paymentResponse.razorpay_signature,
          transactionId: paymentResponse.razorpay_payment_id,
        });
      } else {
        await recordRestaurantGroceryPaymentSuccess(buyerId, {
          createdBy: createdById,
          amount: cartGrandTotal,
          groceryOrderId: orderData?.groceryOrderId,
          masterOrderId: orderData?.masterOrderId,
          razorpayOrderId: paymentResponse.razorpay_order_id,
          razorpayPaymentId: paymentResponse.razorpay_payment_id,
          razorpaySignature: paymentResponse.razorpay_signature,
          transactionId: paymentResponse.razorpay_payment_id,
        });
        await createPurchasesFromGroceryCheckout({
          restaurantId: buyerId,
          createdById,
          cartByStore,
          orderData,
        });
      }

      const orderId = orderData?.masterOrderId ?? orderData?.groceryOrderId;
      clearCart();
      alert(
        orderId
          ? `Order #${orderId} placed and paid successfully${
              isMultiStore ? ` (${cartStoreIds.length} stores)` : ''
            }.`
          : 'Grocery order placed and paid successfully'
      );
      navigate(PURCHASES_PATH);
    } catch (err) {
      if (err instanceof RazorpayCheckoutCancelledError) {
        setSubmitError('Payment cancelled.');
        return;
      }
      if (err instanceof RazorpayPaymentFailedError) {
        window.alert(RAZORPAY_PAYMENT_FAILED_MESSAGE);
        setSubmitError(RAZORPAY_PAYMENT_FAILED_MESSAGE);
        return;
      }
      setSubmitError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Failed to complete grocery checkout.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const buyerRequiredMessage = buyer.isStore
    ? 'Select a store to buy groceries.'
    : 'Select a restaurant to buy groceries.';

  return (
    <RestaurantDashboardLayout activeTab="purchase">
      <div className="buy-grocery-page buy-grocery-cart-page">
        <div className="buy-grocery-page-header">
          <div className="buy-grocery-page-header-left">
            <button
              type="button"
              className="buy-grocery-page-back"
              onClick={() => navigate(BUY_GROCERY_PATH)}
              aria-label="Back to buy groceries"
            >
              <FontAwesomeIcon icon={faArrowLeft} aria-hidden />
            </button>
            <h1 className="buy-grocery-page-title">Cart</h1>
          </div>
        </div>

        {!buyerId ? <p className="buy-grocery-status">{buyerRequiredMessage}</p> : null}

        {submitError ? <p className="buy-grocery-status">{submitError}</p> : null}

        {cartLines.length === 0 ? (
          <div className="buy-grocery-cart-empty">
            <p className="buy-grocery-empty">Your cart is empty.</p>
            <button
              type="button"
              className="buy-grocery-cart-checkout buy-grocery-cart-page-btn"
              onClick={() => navigate(BUY_GROCERY_PATH)}
            >
              Browse stores
            </button>
          </div>
        ) : (
          <>
            {cartByStore.map((group) => (
              <section key={group.storeId} className="buy-grocery-cart-store-group">
                <h2 className="buy-grocery-cart-store-title">{group.storeName}</h2>
                <ul className="buy-grocery-cart-lines">
                  {group.lines.map((line) => {
                    const stockLimit = getGroceryItemStockLimit(line);
                    const atStockLimit =
                      stockLimit != null && Number(line.quantity) >= stockLimit;
                    return (
                    <li key={line.groceryItemId} className="buy-grocery-cart-line">
                      <div className="buy-grocery-cart-line-info">
                        <span className="buy-grocery-cart-line-name">{line.itemName}</span>
                      </div>
                      <div className="buy-grocery-cart-line-actions">
                        <div className="buy-grocery-pos-qty">
                          <button
                            type="button"
                            className="buy-grocery-pos-qty-btn"
                            onClick={() =>
                              updateQuantity(line, line.quantity - 1)
                            }
                            aria-label="Decrease quantity"
                            disabled={isSubmitting}
                          >
                            −
                          </button>
                          <span className="buy-grocery-pos-qty-count">{line.quantity}</span>
                          <button
                            type="button"
                            className="buy-grocery-pos-qty-btn"
                            onClick={() =>
                              updateQuantity(line, line.quantity + 1)
                            }
                            aria-label="Increase quantity"
                            disabled={isSubmitting || atStockLimit}
                          >
                            +
                          </button>
                        </div>
                        <span className="buy-grocery-cart-line-total">
                          ₹{(line.quantity * Number(line.unitPrice || 0)).toFixed(2)}
                        </span>
                      </div>
                    </li>
                    );
                  })}
                </ul>
                <p className="buy-grocery-cart-store-subtotal">
                  Store subtotal: ₹{group.subtotal.toFixed(2)}
                </p>
              </section>
            ))}

            <div className="buy-grocery-cart-grand-total">
              <div className="buy-grocery-cart-fee-row">
                <span className="buy-grocery-cart-fee-label">Platform fee</span>
                <span className="buy-grocery-cart-fee-amount">
                  ₹{cartPlatformFee.toFixed(2)}
                </span>
              </div>
              <div className="buy-grocery-cart-fee-row">
                <span className="buy-grocery-cart-fee-label">
                  GST ({GROCERY_GST_PERCENT}%)
                </span>
                <span className="buy-grocery-cart-fee-amount">₹{cartGst.toFixed(2)}</span>
              </div>
              <div className="buy-grocery-cart-grand-total-row">
                <span className="buy-grocery-cart-grand-total-label">Total</span>
                <strong className="buy-grocery-cart-grand-total-amount">
                  ₹{cartGrandTotal.toFixed(2)}
                </strong>
              </div>
            </div>

            <div className="buy-grocery-cart-page-actions">
              <button
                type="button"
                className="buy-grocery-cart-clear buy-grocery-cart-page-btn"
                onClick={clearCart}
                disabled={isSubmitting}
              >
                Clear cart
              </button>
              <button
                type="button"
                className="buy-grocery-cart-checkout buy-grocery-cart-page-btn"
                onClick={handleCheckout}
                disabled={!buyerId || isSubmitting}
              >
                {isSubmitting ? 'Opening payment…' : 'Place order'}
              </button>
            </div>
          </>
        )}
      </div>
    </RestaurantDashboardLayout>
  );
};

export default BuyGroceryCart;
